(() => {
  'use strict';

  const roots = document.querySelectorAll('[data-f2w-country-detail]');

  if (!roots.length) {
    return;
  }

  const CHAPTERS = {
    cities: { indexType: 'city', storeType: 'city' },
    gyms: { indexType: 'gyms', storeType: 'sports_location' },
    fighters: { indexType: 'fighter_profile', storeType: 'fighter_profile' },
    trainers: { indexType: 'trainer_profile', storeType: 'trainer_profile' },
    events: { indexType: 'event', storeType: 'event' },
    venues: { indexType: 'venue', storeType: 'venue' },
    organizations: { indexType: 'organization', storeType: 'organization' },
    sports: { indexType: 'sport', storeType: 'sport' }
  };

  const TRAINING_TYPES = new Set([
    'gym',
    'school',
    'dojo',
    'dojang'
  ]);

  const NODES_QUERY = `
    query F2WCountryChapter($ids: [ID!]!, $language: LanguageCode!) @inContext(language: $language) {
      nodes(ids: $ids) {
        ... on Metaobject {
          id
          handle
          onlineStoreUrl
          name: field(key: "name") { value }
          displayName: field(key: "display_name") { value }
          resolvedImageUrl: field(key: "resolved_image_url") { value }
          intro: field(key: "intro") { value }
          description: field(key: "description") { value }
          profileIntro: field(key: "profile_intro") { value }
          bio: field(key: "bio") { value }
          city: field(key: "city") { value }
          region: field(key: "region") {
            reference { ... on Metaobject { id name: field(key: "name") { value } } }
          }
          cityReference: field(key: "city_reference") {
            reference { ... on Metaobject { name: field(key: "name") { value } } }
          }
          locationType: field(key: "location_type") { value }
          dateStart: field(key: "date_start") { value }
          dateEnd: field(key: "date_end") { value }
          isHistoric: field(key: "is_historic") { value }
          country: field(key: "country") {
            value
            reference {
              ... on Metaobject {
                id
                name: field(key: "name") { value }
                code: field(key: "code") { value }
              }
            }
          }
          sports: field(key: "sports") {
            references(first: 20) {
              pageInfo { hasNextPage }
              nodes {
                ... on Metaobject {
                  id
                  name: field(key: "name") { value }
                }
              }
            }
          }
        }
      }
    }
  `;

  const FACTS_QUERY = `
    query F2WCountryFacts($after: String, $language: LanguageCode!) @inContext(language: $language) {
      metaobjects(type: "country_fact", first: 250, after: $after) {
        nodes {
          id
          label: field(key: "label") { value }
          factValue: field(key: "value") { value }
          qualifier: field(key: "unit_or_qualifier") { value }
          sortOrder: field(key: "sort_order") { value }
          country: field(key: "country") { value }
          source: field(key: "source") {
            reference {
              ... on Metaobject {
                title: field(key: "title") { value }
                url: field(key: "url") { value }
              }
            }
          }
          sources: field(key: "sources") {
            references(first: 250) {
              nodes {
                ... on Metaobject {
                  title: field(key: "title") { value }
                  url: field(key: "url") { value }
                }
              }
            }
          }
        }
        pageInfo { hasNextPage endCursor }
      }
    }
  `;

  const normalize = (value) => String(value || '').trim();

  const unique = (values) => {
    return [...new Set(values.map(normalize).filter(Boolean))];
  };

  const chunk = (values, size) => {
    const chunks = [];
    for (let index = 0; index < values.length; index += size) {
      chunks.push(values.slice(index, index + size));
    }
    return chunks;
  };

  const nativeUrl = (value) => {
    if (!value) {
      return '';
    }

    try {
      const url = new URL(String(value), window.location.origin);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return '';
      }

      const root = window.Shopify?.routes?.root || '/';
      const prefix = root === '/' ? '' : root.replace(/\/$/, '');
      let path = url.pathname;

      if (/^\/[a-z]{2}(\/|$)/i.test(path)) {
        path = path.replace(/^\/[a-z]{2}/i, '');
      }

      if (prefix && path.startsWith(`${prefix}/`)) {
        path = path.slice(prefix.length);
      }

      return `${prefix}${path}${url.search}${url.hash}`;
    } catch (error) {
      return '';
    }
  };

  const requestQuery = async (endpoint, token, query, variables) => {
    if (!token) {
      throw new Error('Missing Storefront token');
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({ query, variables })
    });

    if (!response.ok) {
      throw new Error(`Storefront request failed: ${response.status}`);
    }

    const payload = await response.json();
    if (payload.errors?.length) {
      throw new Error(payload.errors.map((error) => error.message).join('; '));
    }

    return payload.data;
  };

  // Liquid's metaobject.system.id renders as a numeric ID, while the two
  // generated indexes are keyed by the native Metaobject GID. Resolve only
  // an exact numeric suffix; never match by display name or translated code.
  const countryBucket = (countries, countryId) => {
    const id = normalize(countryId);
    if (!id || !countries) return {};
    if (Object.prototype.hasOwnProperty.call(countries, id)) return countries[id];
    if (!/^\d+$/.test(id)) return {};
    const key = Object.keys(countries).find((candidate) =>
      candidate.startsWith('gid://shopify/Metaobject/') && candidate.endsWith(`/${id}`)
    );
    return key ? countries[key] : {};
  };

  const joinCountryParts = (parts) => {
    if (!Array.isArray(parts)) return parts;
    const allowed = new Set(Object.values(CHAPTERS).map(({ indexType }) => indexType));
    const joined = {};
    for (const part of parts) {
      if (part === null) continue;
      if (typeof part !== 'object' || Array.isArray(part)) {
        throw new Error('Invalid published Country part');
      }
      for (const [type, rows] of Object.entries(part)) {
        if (!allowed.has(type) || !Array.isArray(rows)) {
          throw new Error('Invalid published Country rows');
        }
        if (!joined[type]) joined[type] = [];
        joined[type].push(...rows);
      }
    }
    return joined;
  };

  const joinGymParts = (parts) => {
    if (!Array.isArray(parts)) return parts;
    const joined = { c: '', r: [] };
    for (const part of parts) {
      if (part === null) continue;
      if (typeof part !== 'object' || Array.isArray(part) ||
          typeof part.c !== 'string' || !Array.isArray(part.r)) {
        throw new Error('Invalid published Gym part');
      }
      if (joined.c && part.c && joined.c !== part.c) {
        throw new Error('Conflicting published Gym country codes');
      }
      if (part.c) joined.c = part.c;
      joined.r.push(...part.r);
    }
    return joined;
  };

  const parseCountryIndex = (payload, countryId) => {
    if (!payload || payload.v !== 1 || !payload.countries) {
      throw new Error('Invalid Country geographic index');
    }

    const bucket = countryBucket(payload.countries, countryId);
    const result = {};

    Object.entries(CHAPTERS).forEach(([chapter, config]) => {
      const rows = bucket[config.indexType] || [];
      result[chapter] = rows.map((row) => ({
        id: normalize(row?.[0]),
        cityId: normalize(row?.[1])
      })).filter((row) => row.id);
    });

    return result;
  };

  const parseGymIndex = (payload, countryId) => {
    if (!payload || payload.v !== 1 || !payload.countries) {
      throw new Error('Invalid Gym geographic index');
    }

    const rows = countryBucket(payload.countries, countryId).r || [];
    return rows
      .filter((row) => TRAINING_TYPES.has(normalize(row?.[1]).toLowerCase()))
      .map((row) => ({
        id: normalize(row?.[0]),
        cityId: normalize(row?.[2]),
        cityText: normalize(row?.[3]),
        locationType: normalize(row?.[1])
      }))
      .filter((row) => row.id);
  };

  const labelFromReference = (reference) => {
    return normalize(reference?.name?.value) || normalize(reference?.handle);
  };

  const mapNode = (node, chapter) => {
    if (!node) {
      return null;
    }

    const name = normalize(node.name?.value) || normalize(node.displayName) || normalize(node.handle);
    if (!name) {
      return null;
    }

    const sportReferences = node.sports?.references;
    const sports = unique((sportReferences?.nodes || []).map(labelFromReference)).slice(0, 2);
    // A partial reference connection must never produce misleading filter choices.
    const sportChoices = sportReferences?.pageInfo?.hasNextPage ? [] :
      (sportReferences?.nodes || []).map((sport) => ({ id: normalize(sport.id), label: labelFromReference(sport) }))
        .filter((sport) => sport.id && sport.label);

    const details = [];
    const city = chapter === 'events'
      ? normalize(node.cityReference?.reference?.name?.value)
      : normalize(node.city?.value);
    const locationType = normalize(node.locationType?.value);
    const hasSports = ['gyms', 'fighters', 'trainers', 'sports'].includes(chapter);

    // The Country is already the page context. Prioritize city and two
    // separate sports; use a Gym/school type only when no sport is available.
    if (city) details.push(city);
    if (hasSports) sports.forEach((sport) => details.push(sport));
    if (chapter === 'gyms' && !sports.length && locationType) details.push(locationType);

    let excerpt = normalize(
      node.intro?.value ||
      node.profileIntro?.value ||
      node.bio?.value ||
      node.description?.value
    );

    if (chapter === 'events' && normalize(node.dateStart?.value)) {
      details.unshift(normalize(node.dateStart.value));
    }

    if (excerpt.length > 180) {
      excerpt = `${excerpt.slice(0, 177)}...`;
    }

    return {
      id: normalize(node.id),
      name,
      url: nativeUrl(node.onlineStoreUrl),
      image: normalize(node.resolvedImageUrl?.value),
      details: unique(details),
      excerpt,
      sportChoices,
      regionId: chapter === 'cities' ? normalize(node.region?.reference?.id) : '',
      regionLabel: chapter === 'cities' ? normalize(node.region?.reference?.name?.value) : '',
      sportsComplete: !sportReferences?.pageInfo?.hasNextPage
    };
  };

  const make = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined && text !== null) element.textContent = text;
    return element;
  };

  const renderCard = (item, labels) => {
    const article = make('article', 'f2w-event-card');
    const link = make(item.url ? 'a' : 'div', 'f2w-event-card__link');

    if (item.url) {
      link.href = item.url;
    }

    const imageWrap = make('div', 'f2w-event-card__image');
    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.name;
      image.width = 900;
      image.height = 675;
      image.loading = 'lazy';
      image.decoding = 'async';
      imageWrap.appendChild(image);
    } else {
      const placeholder = make('div', 'f2w-event-card__image--placeholder');
      placeholder.appendChild(make('span', '', item.name.slice(0, 1)));
      imageWrap.appendChild(placeholder);
    }

    const body = make('div', 'f2w-event-card__body');
    const content = make('div', 'f2w-event-card__content');

    if (item.details.length) {
      const topline = make('div', 'f2w-event-card__topline');
      item.details.slice(0, 3).forEach((detail) => {
        topline.appendChild(make('span', 'f2w-event-card__period f2w-event-card__period--upcoming', detail));
      });
      content.appendChild(topline);
    }

    content.appendChild(make('h3', 'f2w-event-card__title', item.name));

    if (item.url) {
      content.appendChild(make('span', 'f2w-event-card__view', labels.view));
    }

    body.appendChild(content);
    link.append(imageWrap, body);
    article.appendChild(link);
    return article;
  };

  const init = (root) => {
    const countryId = normalize(root.dataset.countryId);
    const token = root.dataset.storefrontToken || '';
    const shop = root.dataset.shopDomain || window.Shopify?.shop || window.location.hostname;
    const apiVersion = root.dataset.apiVersion || '2024-10';
    const endpoint = root.dataset.storefrontEndpoint || `https://${shop}/api/${apiVersion}/graphql.json`;
    const language = (root.dataset.language || document.documentElement?.lang || 'nl').split('-')[0].toUpperCase();
    const perPage = Math.max(1, parseInt(root.dataset.perPage, 10) || 24);
    const countFormatter = new Intl.NumberFormat(root.dataset.language || document.documentElement?.lang || 'nl');
    const dateParts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Amsterdam', year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(new Date());
    const dayKey = ['year', 'month', 'day']
      .map((type) => dateParts.find((part) => part.type === type)?.value || '')
      .join('-');
    const countryCode = normalize(root.dataset.countryCode).toUpperCase();
    const indexUrl = root.dataset.indexUrl || '';
    const gymIndexUrl = root.dataset.gymIndexUrl || '';
    const nav = root.querySelector('[data-f2w-country-chapters]');
    const globalStatus = root.querySelector('[data-f2w-country-global-status]');
    const chapterList = root.querySelector('[data-f2w-country-chapter-list]');
    const sections = new Map(
      [...root.querySelectorAll('[data-f2w-country-chapter]')]
        .map((section) => [section.dataset.f2wCountryChapter, section])
    );
    const labels = {
      loading: root.dataset.labelLoading || 'Laden...',
      empty: root.dataset.labelEmpty || 'Geen resultaten gevonden.',
      error: root.dataset.labelError || 'De gegevens konden niet worden geladen.',
      view: root.dataset.labelView || 'Bekijk profiel'
    };

    const states = new Map();
    const tabButtons = new Map();
    let heroGymIndexReady = false;
    let countryIndexReady = false;
    let chapterRecords = {};
    let countryGid = '';
    let observer = null;

    const setGlobalStatus = (text, visible) => {
      if (!globalStatus) return;
      globalStatus.textContent = text || '';
      globalStatus.hidden = !visible;
    };

    const setChapterStatus = (section, text) => {
      const status = section.querySelector('[data-f2w-country-status]');
      if (status) status.textContent = text || '';
    };

    const hydrate = async (chapter, records) => {
      const config = CHAPTERS[chapter];
      const batches = chunk(records.map((record) => record.id), 250);
      const nodes = [];

      for (let index = 0; index < batches.length; index += 3) {
        const group = batches.slice(index, index + 3);
        const results = await Promise.all(
          group.map((ids) => requestQuery(endpoint, token, NODES_QUERY, { ids, language }))
        );
        results.forEach((result, position) => {
          if (!Array.isArray(result?.nodes)) throw new Error('Country chapter nodes unavailable');
          const received = new Set(result.nodes.filter(Boolean).map((node) => normalize(node.id)));
          if (group[position].some((id) => !received.has(id))) throw new Error('Country chapter hydration incomplete');
          nodes.push(...result.nodes.filter(Boolean));
        });
      }

      const byId = new Map(nodes.filter(Boolean).map((node) => [normalize(node.id), node]));
      const matched = records.map((record) => byId.get(record.id)).filter(Boolean);
      if (chapter === 'events') {
        // Match the Events directory's end-date fallback and historic flag.
        // Undated events cannot be confirmed as upcoming, so omit them here.
        return matched
          .filter((node) => {
            const historic = ['true', '1', 'yes'].includes(normalize(node.isHistoric?.value).toLowerCase());
            const referenceDate = normalize(node.dateEnd?.value) || normalize(node.dateStart?.value);
            const eventCountry = normalize(node.country?.reference?.id || node.country?.value).split('/').pop();
            const currentCountry = normalize(countryGid || countryId).split('/').pop();
            return !!currentCountry && eventCountry === currentCountry && !historic &&
              /^\d{4}-\d{2}-\d{2}$/.test(referenceDate) && referenceDate >= dayKey;
          })
          .sort((a, b) => normalize(a.dateStart?.value).localeCompare(normalize(b.dateStart?.value)) ||
            normalize(a.id).localeCompare(normalize(b.id)))
          .map((node) => mapNode(node, chapter))
          .filter(Boolean);
      }
      return records.map((record) => {
        const node = byId.get(record.id);
        const item = mapNode(node, chapter);
        if (item && chapter === 'gyms') {
          item.cityId = record.cityId;
          item.cityLabel = record.cityText;
        }
        return item;
      }).filter(Boolean);
    };

    const renderFilters = (chapter) => {
      const section = sections.get(chapter);
      const state = states.get(chapter);
      const area = section?.querySelector('[data-f2w-country-filters]');
      if (!area || !state) return;
      area.replaceChildren();
      if (chapter === 'cities') {
        const regions = new Map();
        state.items.forEach((item) => {
          if (item.regionId && item.regionLabel) regions.set(item.regionId, { id: item.regionId, label: item.regionLabel });
        });
        if (regions.size) {
          const regionGroup = make('div', 'f2w-country-detail__filter-group');
          regionGroup.appendChild(make('p', 'f2w-fighter-v2__eyebrow f2w-country-detail__filter-heading', root.dataset.labelRegion || ''));
          const choices = make('div', 'f2w-country-detail__filter-choices');
          const addRegion = (id, text) => {
            const button = make('button', 'f2w-event-card__sport', text);
            button.type = 'button';
            button.setAttribute('aria-pressed', (state.region === id).toString());
            button.addEventListener('click', () => {
              state.region = id;
              state.page = 1;
              choices.querySelectorAll('button').forEach((chip) => chip.setAttribute('aria-pressed', (chip === button).toString()));
              renderChapter(chapter);
            });
            choices.appendChild(button);
          };
          addRegion('', root.dataset.labelAllRegions || root.dataset.labelAll || '');
          [...regions.values()].sort((a, b) => a.label.localeCompare(b.label, language.toLowerCase())).forEach((region) => addRegion(region.id, region.label));
          regionGroup.appendChild(choices);
          area.appendChild(regionGroup);
        }
        const group = make('div', 'f2w-country-detail__filter-group');
        const panel = make('div', 'f2w-event-directory__filters');
        const field = make('div', 'f2w-event-directory__filter f2w-event-directory__filter--search');
        field.style.gridColumn = '1 / -1';
        const input = make('input', '');
        input.id = `${section.id}-city-search`;
        input.type = 'search';
        input.autocomplete = 'off';
        input.placeholder = root.dataset.searchPlaceholder || '';
        const label = make('label', '', root.dataset.labelSearch || root.dataset.labelCity);
        label.htmlFor = input.id;
        input.value = state.search || '';
        input.addEventListener('input', () => {
          state.search = input.value;
          state.page = 1;
          renderChapter(chapter);
        });
        field.appendChild(label);
        field.appendChild(input);
        panel.appendChild(field);
        group.appendChild(panel);
        area.appendChild(group);
      }
      const addGroup = (kind, label, choices) => {
        if (choices.length < (kind === 'city' ? 1 : 2)) return;
        const group = make('div', 'f2w-country-detail__filter-group');
        group.appendChild(make('p', 'f2w-fighter-v2__eyebrow f2w-country-detail__filter-heading', label));
        const choicesRow = make('div', 'f2w-country-detail__filter-choices');
        const appendChoice = (id, text) => {
          const button = make('button', 'f2w-event-card__sport', text);
          button.type = 'button';
          button.setAttribute('aria-pressed', (state.filters[kind] === id).toString());
          button.addEventListener('click', () => {
            state.filters[kind] = id;
            if (chapter === 'gyms' && kind === 'sport') {
              state.sportChosen = true;
              state.filters.city = '';
            }
            state.page = 1;
            renderFilters(chapter);
            renderChapter(chapter);
          });
          choicesRow.appendChild(button);
        };
        appendChoice('', kind === 'city' ? (root.dataset.labelAllCities || 'Alle steden') : (root.dataset.labelAll || 'Alles'));
        choices.sort((a, b) => a.label.localeCompare(b.label, language.toLowerCase()));
        const shown = chapter === 'gyms' && kind === 'city' && state.filters.city
          ? choices.filter((choice) => choice.id === state.filters.city) : choices;
        shown.forEach((choice) => appendChoice(choice.id, choice.label));
        group.appendChild(choicesRow);
        area.appendChild(group);
      };
      if (chapter !== 'cities' && chapter !== 'sports' && state.items.every((item) => item.sportsComplete)) {
        const sports = new Map();
        state.items.forEach((item) => item.sportChoices.forEach((sport) => sports.set(sport.id, sport)));
        addGroup('sport', root.dataset.labelSport || 'Sport', [...sports.values()]);
      }
      if (chapter === 'gyms' && state.sportChosen) {
        const cities = new Map();
        state.items.forEach((item) => {
          if (state.filters.sport && !item.sportChoices.some((sport) => sport.id === state.filters.sport)) return;
          if (item.cityId && item.cityLabel) cities.set(item.cityId, { id: item.cityId, label: item.cityLabel });
        });
        addGroup('city', root.dataset.labelCity || 'Stad', [...cities.values()]);
      }
      area.hidden = !area.childElementCount;
    };

    const renderChapter = (chapter) => {
      const section = sections.get(chapter);
      const state = states.get(chapter);
      if (!section || !state) return;

      const grid = section.querySelector('[data-f2w-country-results]');
      if (!grid) return;
      const matching = state.items.filter((item) =>
        (!state.filters.sport || item.sportChoices.some((sport) => sport.id === state.filters.sport)) &&
        (!state.filters.city || item.cityId === state.filters.city) &&
        (chapter !== 'cities' || !state.region || item.regionId === state.region) &&
        (chapter !== 'cities' || !state.search ||
          item.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase(language.toLowerCase()).includes(
            state.search.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase(language.toLowerCase())
          ))
      );
      const pages = Math.ceil(matching.length / perPage);
      state.page = Math.min(state.page, Math.max(1, pages));
      grid.replaceChildren();
      matching.slice((state.page - 1) * perPage, state.page * perPage).forEach((item) => {
        grid.appendChild(renderCard(item, labels));
      });
      setChapterStatus(section, matching.length ? `${countFormatter.format(matching.length)} ${root.dataset.labelResults || 'resultaten'}` : labels.empty);
      const pagination = section.querySelector('[data-f2w-country-pagination]');
      if (!pagination) return;
      pagination.replaceChildren();
      pagination.hidden = pages <= 1;
      if (pages <= 1) return;
      const addPage = (text, page, current, disabled, ariaLabel) => {
        const button = make('button', 'f2w-event-directory__tab', text);
        button.type = 'button';
        button.disabled = disabled;
        button.setAttribute('aria-label', ariaLabel || `${root.dataset.labelPage || 'Pagina'} ${page}`);
        if (current) button.setAttribute('aria-current', 'page');
        else if (!disabled) button.addEventListener('click', () => {
          state.page = page;
          renderChapter(chapter);
          pagination.querySelector('[aria-current="page"]')?.focus({ preventScroll: true });
          grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        return button;
      };
      const pageNumbers = [...new Set([1, state.page - 1, state.page, state.page + 1, pages]
        .filter((page) => page >= 1 && page <= pages))].sort((a, b) => a - b);
      if (state.page > 1) pagination.appendChild(addPage('◀', state.page - 1, false, false, root.dataset.labelPrevious || 'Vorige'));
      let last = 0;
      for (const page of pageNumbers) {
        if (page - last > 1) pagination.appendChild(make('span', '', '…'));
        pagination.appendChild(addPage(String(page), page, page === state.page, false));
        last = page;
      }
      if (state.page < pages) pagination.appendChild(addPage('▶', state.page + 1, false, false, root.dataset.labelNext || 'Volgende'));
    };

    const loadChapter = async (chapter) => {
      const state = states.get(chapter);
      const section = sections.get(chapter);
      if (!state || !section || state.loading || state.loaded) return;

      state.loading = true;
      state.error = false;
      setChapterStatus(section, labels.loading);

      try {
        const items = await hydrate(chapter, state.records);
        state.items = chapter === 'events' ? items : items.sort((a, b) =>
          a.name.localeCompare(b.name, language.toLowerCase()) || a.id.localeCompare(b.id));
        state.loaded = true;
        state.page = 1;
        renderFilters(chapter);
        renderChapter(chapter);
      } catch (error) {
        state.error = true;
        setChapterStatus(section, labels.error);
      } finally {
        state.loading = false;
        if (chapter === 'events') updateHeroStats();
      }
    };

    const activateChapter = (chapter) => {
      sections.forEach((section, key) => {
        section.hidden = key !== chapter;
        const button = tabButtons.get(key);
        if (button) button.setAttribute('aria-selected', key === chapter ? 'true' : 'false');
      });
      if (chapter !== 'introduction' && chapter !== 'country_sport_pages') loadChapter(chapter);
    };

    const updateHeroStats = () => {
      const grid = root.querySelector('[data-country-hero-stat-grid]');
      if (!grid) return;
      grid.querySelectorAll('[data-country-hero-stat]').forEach((row) => {
        const chapter = row.dataset.countryHeroStat;
        const records = chapterRecords[chapter] || [];
        const eventState = chapter === 'events' ? states.get(chapter) : null;
        const count = chapter === 'events' ? (eventState?.loaded ? eventState.items.length : 0) : records.length;
        const link = row.querySelector('[data-country-hero-stat-jump]');
        const value = row.querySelector('[data-country-hero-stat-value]');
        const available = chapter === 'events'
          ? countryIndexReady && (records.length === 0 || !!eventState?.loaded && !eventState.error)
          : chapter !== 'gyms' || heroGymIndexReady;
        const clickable = available && count > 0 && tabButtons.has(chapter);
        if (link) { link.textContent = countFormatter.format(count); link.hidden = !clickable; }
        if (value) { value.textContent = available ? countFormatter.format(count) : '-'; value.hidden = clickable; }
      });
      grid.hidden = false;
    };

    root.querySelectorAll('[data-country-hero-stat-jump]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const chapter = link.dataset.countryHeroStatJump;
        if (!tabButtons.has(chapter)) return;
        event.preventDefault();
        activateChapter(chapter);
        nav?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    const buildNavigation = () => {
      const introduction = sections.get('introduction');
      if (introduction) {
        introduction.setAttribute('role', 'tabpanel');
        const button = make('button', 'f2w-event-card__sport', introduction.dataset.label);
        button.type = 'button';
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', introduction.id);
        button.addEventListener('click', () => activateChapter('introduction'));
        tabButtons.set('introduction', button);
        nav.appendChild(button);
      }
      const countrySportPages = sections.get('country_sport_pages');
      const countrySportPageLinks = countrySportPages?.querySelectorAll('[data-f2w-country-page-link]') || [];
      if (countrySportPages && countrySportPageLinks.length) {
        countrySportPages.hidden = true;
        countrySportPages.setAttribute('role', 'tabpanel');

        const button = make('button', 'f2w-event-card__sport', countrySportPages.dataset.label || '');
        button.type = 'button';
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-selected', 'false');
        button.setAttribute('aria-controls', countrySportPages.id);
        button.addEventListener('click', () => activateChapter('country_sport_pages'));
        tabButtons.set('country_sport_pages', button);
        nav.appendChild(button);
      }

      nav.setAttribute('role', 'tablist');
      Object.keys(CHAPTERS).forEach((chapter) => {
        const records = chapterRecords[chapter] || [];
        if (!records.length) return;

        const section = sections.get(chapter);
        if (!section) return;
        section.hidden = true;
        section.setAttribute('role', 'tabpanel');

        const button = make('button', 'f2w-event-card__sport', section.dataset.label || chapter);
        button.type = 'button';
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-selected', 'false');
        button.setAttribute('aria-controls', section.id);
        button.addEventListener('click', () => activateChapter(chapter));
        tabButtons.set(chapter, button);
        nav.appendChild(button);

        const directoryLink = section.querySelector('[data-f2w-country-view-all]');
        if (directoryLink && directoryLink.getAttribute('href')) {
          // Use the native directory URL. Only apply Country parameters where
          // the live directory loader has been verified to accept that value.
          const filterValue = chapter === 'gyms'
            ? countryCode
            : (chapter === 'fighters' || chapter === 'organizations' ? countryGid : '');
          if (filterValue) {
            const target = new URL(directoryLink.href, window.location.href);
            target.searchParams.set('country', filterValue);
            // The current Fighters directory requires a Sport selection before it
            // loads a country scope. Its native all-sports choice is '__all__'.
            if (chapter === 'fighters') target.searchParams.set('sport', '__all__');
            directoryLink.href = target.href;
          }
          directoryLink.hidden = false;
        }

        states.set(chapter, {
          records,
          items: [],
          page: 1,
          filters: { sport: '', city: '' },
          region: '',
          search: '',
          sportChosen: false,
          loaded: false,
          loading: false,
          error: false
        });
      });

      nav.hidden = !nav.childElementCount;
      if (tabButtons.size) activateChapter(tabButtons.has('introduction') ? 'introduction' : tabButtons.keys().next().value);
    };

    const bindObserver = () => {
      if (!('IntersectionObserver' in window)) {
        states.forEach((state, chapter) => loadChapter(chapter));
        return;
      }

      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const chapter = entry.target.dataset.f2wCountryChapter;
          loadChapter(chapter);
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '500px 0px' });

      sections.forEach((section, chapter) => {
        if (!section.hidden && states.has(chapter)) {
          observer.observe(section);
        }
      });
    };

    const loadFacts = async () => {
      const area = root.querySelector('[data-f2w-country-milestones]');
      const grid = area?.querySelector('[data-f2w-country-fact-results]');
      if (!area || !grid || !countryGid) return;
      const rows = [];
      let after = null;
      do {
        const result = await requestQuery(endpoint, token, FACTS_QUERY, { after, language });
        const connection = result?.metaobjects;
        if (!connection?.pageInfo) throw new Error('Country Fact connection unavailable');
        rows.push(...(connection.nodes || []));
        if (!connection.pageInfo.hasNextPage) break;
        after = connection.pageInfo.endCursor;
        if (!after) throw new Error('Country Fact cursor unavailable');
      } while (true);
      const snapshotIds = new Set((root.dataset.snapshotFactIds || '').split(',').map(normalize).filter(Boolean));
      const matches = rows.filter((node) =>
        normalize(node.country?.value) === countryGid &&
        !snapshotIds.has(normalize(node.id).split('/').pop())
      ).sort((a, b) => Number(a.sortOrder?.value || 0) - Number(b.sortOrder?.value || 0));
      for (const node of matches) {
        const label = normalize(node.label?.value);
        const value = normalize(node.factValue?.value);
        if (!label || !value) continue;
        const item = make('div', 'f2w-fighter-v2__fact');
        item.appendChild(make('dt', '', label));
        item.appendChild(make('dd', '', value));
        const qualifier = normalize(node.qualifier?.value);
        if (qualifier) item.appendChild(make('small', 'f2w-country-detail__fact-qualifier', qualifier));
        const listedSources = node.sources?.references?.nodes || [];
        const sources = listedSources.length ? listedSources : [node.source?.reference].filter(Boolean);
        const sourceLinks = make('small', 'f2w-country-detail__fact-qualifier');
        for (const source of sources) {
          const sourceUrl = normalize(source?.url?.value);
          if (!sourceUrl) continue;
          try {
            const target = new URL(sourceUrl);
            if (!['https:', 'http:'].includes(target.protocol)) continue;
            const link = make('a', 'f2w-country-detail__source-link', normalize(source.title?.value) || root.dataset.sourcesHeading);
            link.href = target.href;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            if (sourceLinks.childElementCount) sourceLinks.appendChild(document.createTextNode(' · '));
            sourceLinks.appendChild(link);
          } catch (error) { /* Ignore invalid external source URLs. */ }
        }
        if (sourceLinks.childElementCount) item.appendChild(sourceLinks);
        grid.appendChild(item);
      }
      area.hidden = !grid.childElementCount;
    };

    const loadIndexes = async () => {
      if (!countryId || !indexUrl) {
        return;
      }

      setGlobalStatus(labels.loading, true);

      const publishedCountry = root.querySelector('[data-f2w-country-index-bucket]');
      let countryPayload = null;
      if (publishedCountry) {
        try {
          const bucket = joinCountryParts(JSON.parse(publishedCountry.textContent || 'null'));
          if (bucket !== null && (typeof bucket !== 'object' || Array.isArray(bucket))) {
            throw new Error('Invalid published Country bucket');
          }
          const countryGidForBucket = `gid://shopify/Metaobject/${countryId}`;
          countryPayload = { v: 1, countries: { [countryGidForBucket]: bucket || {} } };
          parseCountryIndex(countryPayload, countryId);
        } catch (error) {
          console.warn('FightBase Country: published index unavailable; using theme asset.', error);
          countryPayload = null;
        }
      }
      if (!countryPayload) {
        const countryResponse = await fetch(indexUrl, { credentials: 'same-origin' });
        if (!countryResponse.ok) throw new Error('Country index request failed');
        countryPayload = await countryResponse.json();
      }
      countryGid = Object.keys(countryPayload.countries || {}).find((key) =>
        key.startsWith('gid://shopify/Metaobject/') && key.endsWith(`/${countryId}`)
      ) || '';
      chapterRecords = parseCountryIndex(countryPayload, countryId);
      countryIndexReady = true;
      chapterRecords.gyms = [];

      // Gym data is supplementary. A failed Gym index must not hide Fighters,
      // Cities or other chapters already found in the Country index.
      const publishedGym = root.querySelector('[data-f2w-country-gym-index-bucket]');
      if (publishedGym || gymIndexUrl) {
        try {
          let gymPayload = null;
          if (publishedGym) {
            try {
              const bucket = joinGymParts(JSON.parse(publishedGym.textContent || 'null'));
              if (bucket !== null && (!Array.isArray(bucket.r) || typeof bucket.c !== 'string')) {
                throw new Error('Invalid published Gym bucket');
              }
              const gymCountryGid = `gid://shopify/Metaobject/${countryId}`;
              gymPayload = { v: 1, countries: { [gymCountryGid]: bucket || { c: '', r: [] } } };
              parseGymIndex(gymPayload, countryId);
            } catch (error) {
              console.warn('FightBase Country: published Gym index unavailable; using theme asset.', error);
              gymPayload = null;
            }
          }
          if (!gymPayload) {
            const gymResponse = await fetch(gymIndexUrl, { credentials: 'same-origin' });
            if (!gymResponse.ok) throw new Error('Gym index request failed');
            gymPayload = await gymResponse.json();
          }
          chapterRecords.gyms = parseGymIndex(gymPayload, countryId);
          heroGymIndexReady = true;
        } catch (error) {
          console.warn('FightBase Country: Gym index unavailable; other chapters remain available.', error);
        }
      }
      setGlobalStatus('', false);
    };

    loadIndexes()
      .then(() => {
        buildNavigation();
        updateHeroStats();
        if (root.querySelector('[data-country-hero-stat="events"]') && tabButtons.has('events')) loadChapter('events');
        loadFacts().catch((error) => console.warn('FightBase Country facts unavailable.', error));
      })
      .catch((error) => {
        setGlobalStatus(labels.error, true);
        if (chapterList) chapterList.dataset.f2wCountryError = 'true';
      });
  };

  roots.forEach(init);
})();
















